import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Aviso } from '@/components/ui/aviso';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { prixiaService } from '../services/prixia-service';
import type { EjeDeTiendas, Lector, PlataformaDetectada } from '../types/prixia.types';
import { codigoDeMoneda, etiquetasDeLectores, nombreDeLector } from '../lib/prixia-textos';

interface Props {
  abierto: boolean;
  onClose: () => void;
  onCreada: (storeId: string) => void;
}

const VACIO = { nombre: '', url: '', scraperType: 'auto', logoUrl: '', moneda: '', ejeId: '' };
const DETECTAR: Lector = { type: 'auto', name: 'Detectar sola' };
/** Las plataformas que se reconocen preguntándole a la tienda (L7). */
const PLATAFORMAS: Record<string, string> = {
  shopify: 'Shopify',
  woocommerce: 'WooCommerce',
  vtex: 'VTEX',
  magento: 'Magento',
};

/**
 * Alta de una tienda en el catálogo de Prixia (C3: sólo plataforma). Sin empresas: se
 * habilita desde la ficha de cada empresa (plan 297, P10). Si la dirección ya está en
 * el catálogo (con o sin «www»), no se duplica: lleva a su ficha.
 */
export function AltaTiendaPrixiaDialog({ abierto, onClose, onCreada }: Props) {
  const { toast } = useToast();
  const [lectores, setLectores] = useState<Lector[]>([DETECTAR]);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // L7 (vault 279, PD-55): lo que dijo la tienda al preguntarle cómo se lee.
  const [detectada, setDetectada] = useState<PlataformaDetectada | null>(null);
  const [detectando, setDetectando] = useState(false);
  // Reglas de la demo (343): el rubro de la tienda. Sólo los activos; sin rubro es válido.
  const [ejes, setEjes] = useState<EjeDeTiendas[]>([]);
  const [rubroSugerido, setRubroSugerido] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;
    prixiaService
      .ejes()
      .then((e) => setEjes(e.filter((x) => x.activo)))
      .catch(() => setEjes([]));
    prixiaService
      .lectores()
      .then((l) => setLectores(l.length > 0 ? etiquetasDeLectores(l) : [DETECTAR]))
      .catch(() => {
        setLectores([DETECTAR]);
        setError('No se pudo leer la lista de lectores: queda «Detectar sola».');
      });
  }, [abierto]);

  const cerrar = () => {
    setForm(VACIO);
    setError(null);
    setDetectada(null);
    setRubroSugerido(null);
    onClose();
  };

  const detectar = async () => {
    setDetectando(true);
    setError(null);
    try {
      const d = await prixiaService.detectar(form.url.trim());
      setDetectada(d);
      // Ronda B (punto 19): la moneda que la tienda publica es una propuesta; se edita.
      // El rubro sugerido por el nombre del sitio se propone sólo si todavía no se eligió uno.
      setRubroSugerido(d.ejeSugerido ?? null);
      setForm((f) => ({
        ...f,
        scraperType: d.scraperType,
        moneda: d.moneda ?? f.moneda,
        ejeId: f.ejeId || d.ejeSugerido || '',
      }));
      if (!lectores.some((l) => l.type === d.scraperType)) {
        setLectores((ls) => [...ls, { type: d.scraperType, name: nombreDeLector(d.scraperType) }]);
      }
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo preguntar a la tienda cómo se lee.'));
    } finally {
      setDetectando(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const nombre = form.nombre.trim();
      const moneda = codigoDeMoneda(form.moneda);
      const r = await prixiaService.alta({
        nombre,
        url: form.url.trim(),
        scraperType: form.scraperType,
        logoUrl: form.logoUrl.trim() || null,
        ...(moneda ? { moneda } : {}),
        ...(form.ejeId ? { ejeId: form.ejeId } : {}),
      });
      toast({
        title: r.nueva
          ? `${nombre} entró al catálogo de Prixia`
          : 'Esa tienda ya estaba en el catálogo',
        description: r.nueva
          ? 'El primer chequeo se hace barrido. El barrido semanal nace apagado: se enciende desde su calendario.'
          : 'Se mantuvieron su nombre y su forma de leer; se cambian desde su ficha.',
      });
      setForm(VACIO);
      onCreada(r.storeId);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo agregar la tienda.'));
    } finally {
      setGuardando(false);
    }
  };

  const monedaValida = codigoDeMoneda(form.moneda) !== null;
  const listo = form.nombre.trim().length >= 2 && form.url.trim().length >= 4 && monedaValida;

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && cerrar()}>
      <DialogContent aria-describedby={undefined} className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar tienda al catálogo de Prixia</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="alta-prixia-nombre">Nombre</Label>
            <Input
              id="alta-prixia-nombre"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Dismac"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alta-prixia-url">Dirección del sitio</Label>
            <Input
              id="alta-prixia-url"
              value={form.url}
              onChange={(e) => {
                // Ronda B (T7-2): otra dirección puede ser otra plataforma; lo detectado
                // para la anterior ya no vale y la forma de leer vuelve a «Detectar sola».
                setForm({
                  ...form,
                  url: e.target.value,
                  scraperType: detectada ? 'auto' : form.scraperType,
                  // Ronda B (punto 19): la moneda propuesta para la otra dirección tampoco.
                  moneda: detectada?.moneda && form.moneda === detectada.moneda ? '' : form.moneda,
                });
                setDetectada(null);
              }}
              placeholder="https://www.dismac.com.bo"
            />
            <p className="text-xs text-muted-foreground">
              Con y sin «www» es la misma tienda: si ya está en el catálogo, no se duplica.
            </p>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="alta-prixia-lector">Cómo se lee</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={detectando || form.url.trim().length < 4}
                onClick={() => void detectar()}
              >
                {detectando ? 'Preguntando a la tienda…' : 'Detectar'}
              </Button>
            </div>
            <SelectNativo
              id="alta-prixia-lector"
              className="w-full"
              value={form.scraperType}
              onChange={(e) => setForm({ ...form, scraperType: e.target.value })}
            >
              {lectores.map((l) => (
                <option key={l.type} value={l.type}>
                  {l.name}
                </option>
              ))}
            </SelectNativo>
            {detectada &&
              (detectada.aviso ? (
                <Aviso tono="alerta">{detectada.aviso}</Aviso>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {detectada.como === 'DIRECCION'
                    ? 'Prixia ya tiene un lector para este sitio.'
                    : `Es una tienda ${PLATAFORMAS[detectada.scraperType] ?? detectada.scraperType}: se lee sin navegador y con chequeo diario.`}
                </p>
              ))}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alta-prixia-rubro">
              Rubro para la prueba <span className="text-muted-foreground">(opcional)</span>
            </Label>
            <SelectNativo
              id="alta-prixia-rubro"
              className="w-full"
              value={form.ejeId}
              onChange={(e) => setForm({ ...form, ejeId: e.target.value })}
            >
              <option value="">Sin rubro</option>
              {ejes.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </SelectNativo>
            <p className="text-xs text-muted-foreground">
              {rubroSugerido && form.ejeId === rubroSugerido
                ? 'Lo propone el nombre del sitio: confirmalo o elegí otro.'
                : 'Con rubro, la tienda se ofrece a las empresas en prueba que elijan ese rubro. Sin rubro, aparece en «Otras tiendas».'}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alta-prixia-moneda">
              Moneda en que publica sus precios{' '}
              <span className="text-muted-foreground">(tres letras)</span>
            </Label>
            <Input
              id="alta-prixia-moneda"
              className="w-28 uppercase"
              maxLength={3}
              value={form.moneda}
              onChange={(e) => setForm({ ...form, moneda: e.target.value })}
              placeholder="USD"
              aria-invalid={!monedaValida}
            />
            <p className="text-xs text-muted-foreground">
              {!monedaValida
                ? 'Son las tres letras del código de la moneda (USD, EUR, PEN).'
                : detectada?.moneda
                  ? `La tienda publica sus precios en ${detectada.moneda}. Si no es así, corregila.`
                  : detectada
                    ? 'No encontramos en qué moneda publica: escribila si la sabés.'
                    : 'Con la moneda, Prixia no toma un precio publicado en otra. Si no la sabés, dejala vacía.'}
            </p>
            <p className="text-xs text-muted-foreground">
              Si la tienda cambia sus precios según el país de quien la visita, elegí la moneda en
              que publica para su mercado. Si una página llega en otra moneda, ese precio queda sin
              dato.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="alta-prixia-logo">
              Logo <span className="text-muted-foreground">(opcional)</span>
            </Label>
            <Input
              id="alta-prixia-logo"
              value={form.logoUrl}
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              placeholder="https://…/logo.png"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Después de agregarla, habilitala a cada empresa desde su ficha («Tiendas disponibles»).
          </p>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={cerrar}>
              Cancelar
            </Button>
            <Button onClick={() => void guardar()} disabled={guardando || !listo}>
              {guardando ? 'Agregando…' : 'Agregar al catálogo'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AltaTiendaPrixiaDialog;
